import { useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { CheckinOrderFragment, CheckoutOrderFragment, FlightFragment, OrderStatus } from '@websky/graphql';
import { Mode, WithTypename } from '../../types';
import { useIataCode, useToggleable } from '../../hooks';
import { ModeContext } from '../../context';
import { getOrder } from '../../Checkout/store/order/selectors';
import { getCheckinOrder } from '../../Modules/Checkin/store/order/selectors';
import { useConsentsCheckbox } from './Checkbox/Checkbox.hook';
import { isFlightFareM2, SOGAZ_INSURANCE_CODES } from '../../utils';
import { CheckoutState } from '../../Checkout/store/reducers';
import { CheckinState } from '../../Modules/Checkin/store/reducers';

const UNAVAILABLE_IATAS = ['A9', 'RM', 'JI', '5G', 'SZ', '2S', 'WZ'];

export const useConsents = () => {
	const { t } = useTranslation('Consents');

	const { isOpen: isFareModalOpen, toggle: toggleFareModal, close: closeFareModal } = useToggleable(false);

	const mode = useContext(ModeContext);
	const iataCode = useIataCode();

	const order = useSelector<CheckoutState | CheckinState, CheckoutOrderFragment | CheckinOrderFragment>(state =>
		mode === Mode.Checkin ? getCheckinOrder(state as CheckinState) : getOrder(state as CheckoutState)
	);

	const isIncludeTransferTariff =
		mode !== Mode.Checkin &&
		(order as CheckoutOrderFragment).flight.fares?.some(fare =>
			isFlightFareM2(fare as WithTypename<FlightFragment['fares'][0]>)
		);

	const services = order.priceToPay;

	const isNeedToShowGeneralRules = services.total?.amount > 0;
	const isNeedToShowInsurancesRules = services.insurance?.amount > 0;
	const isNeedToShowBaggageRules = services.baggage?.amount > 0;

	const insurancesProductsLinks = useMemo(() => {
		if (!order?.additionalServices?.insurances?.selectedInsurances?.length) {
			return '';
		}

		const linksMap = new Map<string, string>();

		order.additionalServices.insurances.selectedInsurances.forEach(insurance => {
			const code = insurance.insuranceProgram.code;

			if (!linksMap.has(code)) {
				const insuranceName = t('agreement_additionalInsuranceName', {
					name: insurance.insuranceProgram.name
				});

				const linkValue = insurance.insuranceProgram?.urlToOffer
					? `<a href='${insurance.insuranceProgram?.urlToOffer}' target="_blank">${insuranceName}</a>`
					: insuranceName;

				const formatInsurance = t('agreement_additionalInsuranceRules', {
					link: linkValue
				});

				linksMap.set(code, ` <span>${formatInsurance}</span>`);
			}
		});

		return [...linksMap.values()].join('');
	}, [order?.additionalServices?.insurances?.selectedInsurances]);

	const isAlphaInsuranceSelected = order?.additionalServices?.insurances?.selectedInsurances.some(
			insurance => !SOGAZ_INSURANCE_CODES.includes(insurance.insuranceProgram?.code)
		),
		isSogazInsuranceSelected = order?.additionalServices?.insurances?.selectedInsurances.some(insurance =>
			SOGAZ_INSURANCE_CODES.includes(insurance.insuranceProgram?.code)
		);

	const isNeedToShowGdsRules =
		[services?.vip, services?.businessLounge, services?.seats, services?.meals, services?.baggage].some(
			price => price?.amount > 0
		) && services.total?.amount > 0;

	const insurancesRulesStr = isNeedToShowInsurancesRules
		? t('agreement_insurancesRules', {
				insurancesProductsLinks,
				// will be ignored unless custom translate contains aliases below (WZ theme)
				alphaGeneralRules: isAlphaInsuranceSelected ? t('agreement_insurancesRules_alpha') : '',
				sogazGeneralRules: isSogazInsuranceSelected ? t('agreement_insurancesRules_sogaz') : ''
		  })
		: '';

	const interpolatedRulesLink = t('agreement_interpolation', {
		generalRules: isNeedToShowGeneralRules ? t('agreement_generalRules') : '',
		insurancesRules: insurancesRulesStr,
		additionalServiceRules: isNeedToShowGdsRules ? t('agreement_gdsRules') : '',
		baggageRules: isNeedToShowBaggageRules ? t('agreement_baggageRules') : ''
	});

	const rules = interpolatedRulesLink
		? t('agreement_rules', {
				links: interpolatedRulesLink,
				insurancesRules: insurancesRulesStr,
				generalRules: isNeedToShowGeneralRules ? t('agreement_generalRules') : '',
				alphaGeneralRules: isAlphaInsuranceSelected ? t('agreement_insurancesRules_alpha') : '',
				sogazGeneralRules: isSogazInsuranceSelected ? t('agreement_insurancesRules_sogaz') : '',
				additionalServiceRules: isNeedToShowGdsRules ? t('agreement_gdsRules') : '',
				baggageRules: isNeedToShowBaggageRules ? t('agreement_baggageRules') : ''
		  })
		: '';

	const transferRules = t('agreement_transferRules', { links: t('agreement_transferLink') });

	const isNeedToShowRules = useMemo(() => {
		const availableOrderStatus = order.status !== OrderStatus.Cancelled;
		const availableByIata = !UNAVAILABLE_IATAS.includes(iataCode);

		return availableOrderStatus && availableByIata && !!rules;
	}, [iataCode, rules, order.status]);

	const isNeedToShowTransferRules = useMemo(() => {
		return order.status === OrderStatus.Booked && isIncludeTransferTariff && !!transferRules;
	}, [transferRules, isIncludeTransferTariff, order.status]);

	const commonConsentsCheckbox = useConsentsCheckbox({ needConfirm: isNeedToShowRules });
	const transferConsentsCheckbox = useConsentsCheckbox({ needConfirm: isNeedToShowTransferRules });

	const onClick = (e: React.MouseEvent<HTMLDivElement>) => {
		const target = e.target as HTMLSpanElement;

		if (target.hasAttribute('data-type')) {
			e.preventDefault();

			const linkType = target.getAttribute('data-type');
			if (linkType === 'termsOfTariff') {
				toggleFareModal();
			}
		}
	};

	const onConfirm = useCallback(() => {
		if (commonConsentsCheckbox.isNeedConfirm) {
			commonConsentsCheckbox.onConfirm();
		}

		if (transferConsentsCheckbox.isNeedConfirm) {
			transferConsentsCheckbox.onConfirm();
		}
	}, [
		commonConsentsCheckbox.isNeedConfirm,
		commonConsentsCheckbox.onConfirm,
		transferConsentsCheckbox.isNeedConfirm,
		transferConsentsCheckbox.onConfirm
	]);

	return {
		// data
		order,
		rules,
		transferRules,
		isNeedToShowRules,
		isNeedToShowTransferRules,
		isFareModalOpen,

		// checkboxes
		commonConsentsCheckbox,
		transferConsentsCheckbox,

		// methods
		onClick,
		onConfirm: commonConsentsCheckbox.isNeedConfirm || transferConsentsCheckbox.isNeedConfirm ? onConfirm : null,
		toggleFareModal,
		closeFareModal
	};
};
